import type { FirstTierSupplier, part } from "@/api/supplierService";
import Breadcrumb from "@/components/Breadcrumb";
import BackButton from "@/components/buttons/BackButton";
import BreakdownSidebar from "@/components/first-tier-supplier/BreakdownSidebar";
import {
  useGetFirstTierSuppliers,
  useGetPartBySupplier,
} from "@/hooks/supplierHooks";
import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BreakdownTable from "./BreakdownTable";
import {
  useGetChildPartV1,
  useGetPartPriceHistory,
  useGetChildPartV2,
  useCreateChildPartV2,
  useUpdateChildPartV2,
  useGetPartProcessV2,
  useGetPartOtherProcessV2,
  useUpdatePartOtherProcessV2Detail,
  useUpdatePartProcessV2Detail,
  useDeleteChildPartV2,
} from "@/hooks/breakdownHooks";
import PartPriceHistoryTable from "@/components/first-tier-supplier/PartPriceHistoryTable";
import type {
  childPartV2,
  childPartV2Param,
  partOtherProcess,
  partProcess,
} from "@/api/breakdownService";
import {
  DefaultSecondaryButton,
  NormalSecondaryButton,
  DefaultPrimaryButton,
} from "@/styles/globalstyles";
import ManageMaterialProcessModal from "@/components/first-tier-supplier/breakdown-process/ManageMaterialProcessModal";
import ManagePartProcessModal from "@/components/first-tier-supplier/breakdown-process/ManagePartProcessModal";
import ManageChildPartProcessModal from "@/components/first-tier-supplier/breakdown-process/ManageChildPartProcessModal";
import { Add, Delete, InsertDriveFile } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import PartProcess from "@/components/first-tier-supplier/breakdown/PartProcess";
import ManagePartOtherProcessModal from "@/components/first-tier-supplier/breakdown-process/ManagePartOtherProcessModal";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { useToast } from "@/hooks/useToast";
import { ConfirmAlert, ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";
import { useGetMaterialCodeByFirstTierSupplier } from "@/hooks/materialSupplierHook";
import { useFlagPartHandler } from "@/hooks/useFlagPartHandler";
import { parseDecimal } from "@/utils/inputParse";
import UnFlaggedModal from "@/components/flagged-part/UnFlaggedModal";
import BreakdownInfo from "@/components/first-tier-supplier/BreakdownInfo";
import { usePermissions } from "@/hooks/usePermissions";
import { exportPartToExcel, exportPartV1ToExcel } from "@/utils/excel/index";

const BreakdownManagement = () => {
  const canEdit = usePermissions().canEdit("breakdown");
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [menu, setMenu] = useState<string>("child-part");
  const [supplier, setSupplier] = useState<FirstTierSupplier | null>(null);
  const [partInfo, setPartInfo] = useState<part | null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isEditingPartProcess, setIsEditingPartProcess] =
    useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [newChildPart, setNewChildPart] = useState<childPartV2Param | null>(
    null,
  );
  const [selectedChildPartId, setSelectedChildPartId] = useState<Array<number>>(
    [],
  );
  const [editedChildParts, setEditedChildParts] = useState<
    Array<childPartV2Param>
  >([]);
  const [flaggedReason, setFlaggedReason] = useState<Set<string>>(new Set());
  const [formDataPartProcess, setFormDataPartProcess] = useState<
    partProcess | partOtherProcess
  >({
    admin_oem_part_process: 0,
    admin_spare_part_process: 0,
    profit_oem_part_process: 0,
    profit_spare_part_process: 0,
    total_oem_part_process: 0,
    total_spare_part_process: 0,
    oem_part_process: [],
    spare_part_process: [],
    flagged_reason: [],
  });
  const [manageMaterialProcessModalOpen, setManageMaterialProcessModalOpen] =
    useState<boolean>(false);
  const [managePartProcessModalOpen, setManagePartProcessModalOpen] =
    useState<boolean>(false);
  const [manageChildPartProcessModalOpen, setManageChildPartProcessModalOpen] =
    useState<boolean>(false);
  const [managePartOtherProcessModalOpen, setManagePartOtherProcessModalOpen] =
    useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<
    Map<number, Set<string>>
  >(new Map());
  const [isOpenUnFlagModal, setIsOpenUnFlagModal] = useState<boolean>(false);

  const { supplierCode, version, partId } = useParams<{
    supplierCode: string;
    version: "v1" | "v2";
    partId: string;
  }>();

  const {
    data: firstTierSuppliers,
    isError: isFirstTierSuppliersError,
  } = useGetFirstTierSuppliers({
    skip: 0,
    limit: 2000,
  });

  const {
    data: partsData,
    isLoading: isPartsDataLoading,
    isError: isPartsDataError,
  } = useGetPartBySupplier(supplierCode || "", version || "v2", true);

  const {
    data: childPartsV1,
    isLoading: isChildPartsV1Loading,
    isError: isChildPartsV1Error,
  } = useGetChildPartV1(partId ? Number(partId) : 0, version);

  const {
    data: childPartsV2,
    isLoading: isChildPartsV2Loading,
    isError: isChildPartsV2Error,
  } = useGetChildPartV2(partId ? Number(partId) : 0, version);

  const [isFetchingForDownload, setIsFetchingForDownload] =
    useState<boolean>(false);

  const {
    data: partProcessV2,
    isLoading: isPartProcessV2Loading,
    isError: isPartProcessV2Error,
    refetch: refetchPartProcessV2,
  } = useGetPartProcessV2(
    partId ? Number(partId) : 0,
    menu === "part-process" || isFetchingForDownload,
  );

  const {
    data: partOtherProcessV2,
    isLoading: isPartOtherProcessV2Loading,
    isError: isPartOtherProcessV2Error,
    refetch: refetchPartOtherProcessV2,
  } = useGetPartOtherProcessV2(
    partId ? Number(partId) : 0,
    menu === "part-other-process" || isFetchingForDownload,
  );

  const {
    data: partPriceHistory,
    isLoading: isPartPriceHistoryLoading,
    isError: isPartPriceHistoryError,
  } = useGetPartPriceHistory(partId ? Number(partId) : 0, version || "v2");

  const {
    data: materialCodes,
    isLoading: isMaterialCodesLoading,
    isError: isMaterialCodesError,
  } = useGetMaterialCodeByFirstTierSupplier(supplierCode || "");

  const { mutate: createChildPartV2, isPending: isCreatingChildPartV2 } =
    useCreateChildPartV2();

  const { mutate: updateChildPartV2, isPending: isUpdatingChildPartV2 } =
    useUpdateChildPartV2();

  const {
    mutate: updatePartProcessV2Detail,
    isPending: isUpdatingPartProcessV2Detail,
  } = useUpdatePartProcessV2Detail();

  const {
    mutate: updatePartOtherProcessV2Detail,
    isPending: isUpdatingPartOtherProcessV2Detail,
  } = useUpdatePartOtherProcessV2Detail();

  const { mutate: deleteChildPartV2, isPending: isDeletingChildPartV2 } =
    useDeleteChildPartV2();

  const { handleFlagPartById, isFlagging } = useFlagPartHandler({
    onOpenUnflagModal: () => setIsOpenUnFlagModal(true),
  });

  useQueryHandler({
    isFetching:
      isCreatingChildPartV2 ||
      isUpdatingChildPartV2 ||
      isMaterialCodesLoading ||
      isPartPriceHistoryLoading ||
      isPartsDataLoading ||
      isUpdatingPartProcessV2Detail ||
      isUpdatingPartOtherProcessV2Detail ||
      isFlagging ||
      isDeletingChildPartV2 ||
      isFetchingForDownload,
    isError:
      isMaterialCodesError ||
      isPartPriceHistoryError ||
      isChildPartsV2Error ||
      isChildPartsV1Error ||
      isPartsDataError ||
      isFirstTierSuppliersError ||
      isPartProcessV2Error ||
      isPartOtherProcessV2Error,
  });

  useEffect(() => {
    document.title = "First Tier Supplier | AMPA";
  }, []);

  const getNewChildPartNo = (): string => {
    if (childPartsV2 && childPartsV2.length > 0) {
      const maxNo = Math.max(
        ...childPartsV2.map((part) => {
          return parseInt(part?.child_part_no?.split(".")[1], 10) || 0;
        }),
      );
      return `${partInfo?.part_number}.${maxNo + 1}`;
    }
    return `${partInfo?.part_number}.1`;
  };

  useEffect(() => {
    if (firstTierSuppliers && supplierCode) {
      const foundSupplier = firstTierSuppliers.find(
        (sup) => sup.supplier_code === supplierCode,
      );
      setSupplier(foundSupplier || null);

      if (
        !foundSupplier ||
        !foundSupplier.template_type ||
        foundSupplier.template_type.toLowerCase() !== "part"
      ) {
        navigate("/breakdown");
      }
    }
  }, [supplierCode, firstTierSuppliers, navigate]);

  useEffect(() => {
    if (partsData && partId && supplierCode) {
      const foundPart = partsData.find((p) => p.part_id === Number(partId));
      setPartInfo(foundPart || null);

      if (!foundPart) {
        navigate("/breakdown/" + supplierCode);
      }
    }
  }, [partsData, partId, navigate, supplierCode]);

  const { filteredChildParts, totalChildPartsCount } = useMemo(() => {
    const childParts = version === "v1" ? childPartsV1 : childPartsV2;
    if (!childParts) return { filteredChildParts: [], totalChildPartsCount: 0 };
    const filtered = childParts.filter(
      (part) =>
        part.child_part_no
          ?.toLowerCase()
          .includes(searchFilter?.toLowerCase()) ||
        part.child_part_name
          ?.toLowerCase()
          .includes(searchFilter?.toLowerCase()) ||
        part.child_part_info.material_code
          ?.toLowerCase()
          .includes(searchFilter?.toLowerCase()),
    );
    const totalChildPartsCount = filtered.length;
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return { filteredChildParts: filtered.slice(startIndex, endIndex), totalChildPartsCount };
  }, [version, childPartsV1, childPartsV2, page, rowsPerPage, searchFilter]);

  const handleAddChildPart = () => {
    setPage(0);
    setIsAdding(true);
    setNewChildPart({
      no: getNewChildPartNo(),
      name: "",
      qty: 1,
      material_id: 0,
      amount: 1,
      ratio: 100,
      volume: 0,
      consumption: 0,
      part_weight: 0,
      scrap_price: 0,
      return_scrap_price: 0,
      material_price: 0,
      material_cost: 0,
      is_purchase_part: false,
      admin: 0,
      profit: 0,
      percent: 0,
      second_tier_process_comment: "",
      child_part_material_second_tier_processes_v2: [],
      other_process_comment: "",
      child_part_material_other_processes_v2: [],
      child_part_processes_v2: [],
    });
  };

  const handleEditChildPart = () => {
    setIsEditing(true);
    const editChildPart: childPartV2[] =
      childPartsV2?.filter((cp) => selectedChildPartId.includes(cp.id)) || [];
    const editChildPartParam: childPartV2Param[] = editChildPart?.map(
      (cp: childPartV2) => {
        const material = materialCodes?.find(
          (mc) => mc.material_code === cp.child_part_info.material_code,
        );
        return {
          child_part_v2_id: cp.id,
          no: cp.child_part_no,
          name: cp.child_part_name,
          qty: cp.child_part_info.qty || 1,
          material_id: material?.material_id || 0,
          amount: cp.child_part_info.amount || 1,
          ratio: cp.child_part_info.ratio || 100,
          volume: material?.volume || 0,
          consumption: parseDecimal(String(cp.child_part_info.consumption || 0), 3),
          part_weight: cp.child_part_info.part_weight || 0,
          scrap_price: material?.return_scrap_price || 0,
          return_scrap_price:
            parseDecimal(String(cp.child_part_material_cost.scrap_price), 3) ||
            0,
          material_price: parseDecimal(String(material?.material_price || 0), 2),
          material_cost: cp.child_part_material_cost.material_cost || 0,
          is_purchase_part: cp.child_part_material_cost.is_purchased || false,
          admin: cp.child_part_material_cost.admin || 0,
          profit: cp.child_part_material_cost.profit || 0,
          percent: cp.child_part_material_cost.percent || 0,
          second_tier_process_comment:
            cp.child_part_material_cost.second_tier_process_comment || "",
          child_part_material_second_tier_processes_v2:
            cp.child_part_material_cost.second_tier_process || [],
          other_process_comment:
            cp.child_part_material_cost.other_process_comment || "",
          child_part_material_other_processes_v2:
            cp.child_part_material_cost.other_process || [],
          child_part_processes_v2: Array.isArray(
            cp.child_part_process.cp_process,
          )
            ? cp.child_part_process.cp_process
            : [],
        };
      },
    );
    setEditedChildParts(editChildPartParam || []);
  };

  const handleEditPartProcess = () => {
    setIsEditingPartProcess(true);
  };

  const validateChildPart = (childPart: childPartV2Param): Set<string> => {
    const errors = new Set<string>();

    if (!childPart.name || childPart.name.trim() === "") {
      errors.add("name");
    }

    if (!childPart.material_id || childPart.material_id === 0) {
      errors.add("material_id");
    }

    return errors;
  };

  const validateSelectedChildPart = (
    childParts: Array<childPartV2Param>,
  ): boolean => {
    const errorsMap = new Map<number, Set<string>>();
    let hasErrors = false;

    childParts.forEach((childPart) => {
      const childPartId = childPart.child_part_v2_id || 0;
      const errors = validateChildPart(childPart);
      if (errors.size > 0) {
        errorsMap.set(childPartId, errors);
        hasErrors = true;
      }
    });

    setValidationErrors(errorsMap);
    return !hasErrors;
  };

  const handleSaveChildPart = () => {
    if (isAdding) {
      const errors = validateChildPart(newChildPart as childPartV2Param);
      if (errors.size > 0) {
        const errorsMap = new Map<number, Set<string>>();
        errorsMap.set(0, errors);
        setValidationErrors(errorsMap);
        ErrorAlert("Error", "กรุณากรอกข้อมูลให้ครบถ้วน");
        return;
      }

      createChildPartV2(
        {
          partId: partId ? Number(partId) : 0,
          supplierCode: supplierCode || "",
          params: newChildPart as childPartV2Param,
          flagged_reason: new Set(["Add Child Part"]),
        },
        {
          onSuccess: () => {
            showToast("เพิ่ม Child part สำเร็จ", "success");
            setIsAdding(false);
            setNewChildPart(null);
          },
          onError: (error) => {
            console.error("Error creating child part:", error);
            ErrorAlert("Error", getAxiosErrorMessage(error));
          },
        },
      );
    }
    if (isEditing) {
      console.log("Updating child part:", editedChildParts);

      const errors = validateSelectedChildPart(editedChildParts);
      if (!errors) {
        ErrorAlert("Error", "กรุณากรอกข้อมูลให้ครบถ้วน");
        return;
      }

      updateChildPartV2(
        {
          partId: partId ? Number(partId) : 0,
          supplierCode: supplierCode || "",
          params: editedChildParts,
          flagged_reason: flaggedReason,
        },
        {
          onSuccess: () => {
            showToast("แก้ไข Child part(s) สำเร็จ", "success");
            setIsEditing(false);
            setEditedChildParts([]);
          },
          onError: (error) => {
            console.error("Error updating child parts:", error);
            ErrorAlert("Error", getAxiosErrorMessage(error));
          },
        },
      );
    }
  };

  const handleSavePartProcess = () => {
    if (menu === "part-process") {
      const params: partProcess = formDataPartProcess as partProcess;
      params.flagged_reason = Array.from(flaggedReason);
      updatePartProcessV2Detail(
        {
          partId: partId ? Number(partId) : 0,
          params: params,
        },
        {
          onSuccess: () => {
            showToast("Part process updated successfully", "success");
            setIsEditingPartProcess(false);
          },
          onError: (error) => {
            console.error("Error updating part process:", error);
            ErrorAlert("Error", getAxiosErrorMessage(error));
          },
        },
      );
    } else if (menu === "part-other-process") {
      const params: partOtherProcess = formDataPartProcess as partOtherProcess;
      params.flagged_reason = Array.from(flaggedReason);
      updatePartOtherProcessV2Detail(
        {
          partId: partId ? Number(partId) : 0,
          params: params,
        },
        {
          onSuccess: () => {
            showToast("Part other process updated successfully", "success");
            setIsEditingPartProcess(false);
          },
          onError: (error) => {
            console.error("Error updating part other process:", error);
            ErrorAlert("Error", getAxiosErrorMessage(error));
          },
        },
      );
    }
  };

  const handleDeactivateChildPart = () => {
    ConfirmAlert(
      "Confirm",
      "ต้องการลบ Child Part ที่เลือกใช่หรือไม่?",
      "ยืนยัน",
      "ยกเลิก",
    ).then((result) => {
      if (result) {
        deleteChildPartV2(
          {
            partId: partId ? Number(partId) : 0,
            childPartIdList: selectedChildPartId,
          },
          {
            onSuccess: () => {
              showToast("Child parts ถูกลบสำเร็จ", "success");
              setSelectedChildPartId([]);
            },
            onError: (error) => {
              console.error("Error deleting child part(s):", error);
              ErrorAlert("Error", getAxiosErrorMessage(error));
            },
          },
        );
      }
    });
  };

  const handleFlagPart = () => {
    handleFlagPartById(
      partId ? Number(partId) : 0,
      partInfo?.is_flagged || false,
    );
  };

  const handleDownloadReport = async () => {
    const confirm = await ConfirmAlert(
      "Download Report",
      "ต้องการดาวน์โหลดรายงานใช่หรือไม่?",
      "ยืนยัน",
      "ยกเลิก",
    );
    if (!confirm) return;

    setIsFetchingForDownload(true);
    if (version === "v1") {
      try {
        await exportPartV1ToExcel({
          supplierCode: supplier?.supplier_code || "",
          supplierName: supplier?.supplier_name || "",
          buyerCode: supplier?.buyer_code_list?.join(",") || "",
          partId: partId ? Number(partId) : 0,
          partNo: partInfo?.part_number || "",
          partName: partInfo?.part_name || "",
          ac: partInfo?.ac || "",
          rt: partInfo?.rt || "",
          oemPrice: partInfo?.oem_price?.new_price || 0,
          sparePrice: partInfo?.spare_price?.new_price || 0,
          admin: supplier?.is_admin_pct ? "Percent" : "THB/pcs",
          profit: supplier?.is_profit_pct ? "Percent" : "THB/pcs",
          childParts: childPartsV1 || [],
        });
      } finally {
        setIsFetchingForDownload(false);
      }
    } else {
      try {
        const [processResult, otherProcessResult] = await Promise.all([
          refetchPartProcessV2(),
          refetchPartOtherProcessV2(),
        ]);
        await exportPartToExcel({
          partId: partId ? Number(partId) : 0,
          partNo: partInfo?.part_number || "",
          partName: partInfo?.part_name || "",
          ac: partInfo?.ac || "",
          rt: partInfo?.rt || "",
          oemPrice: partInfo?.oem_price?.new_price || 0,
          sparePrice: partInfo?.spare_price?.new_price || 0,
          supplierName: supplier?.supplier_name || "",
          supplierCode: supplier?.supplier_code || "",
          childPartsV2: childPartsV2 || [],
          partProcessV2: processResult.data,
          partOtherProcessV2: otherProcessResult.data,
        });
      } finally {
        setIsFetchingForDownload(false);
      }
    }
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1, display: "flex", flexDirection: "column" }}>
      <Breadcrumb
        current={partInfo?.part_number || partId}
        previous={[
          { key: "breakdown" },
          {
            key: supplier?.supplier_abbr || "",
            path: `/breakdown/${supplierCode}`,
          },
        ]}
      />
      <Box
        mt={1}
        display={"flex"}
        alignItems="center"
        justifyContent="flex-start"
        gap={2}
      >
        <BackButton to={`/breakdown/${supplierCode}`} />
      </Box>
      <Box mt={1} p={1} flex={1} display="flex" gap={2}>
        <BreakdownSidebar
          oemPrice={partInfo?.oem_price?.new_price || 0}
          sparePrice={partInfo?.spare_price?.new_price || 0}
          childPartCount={
            version === "v1"
              ? childPartsV1
                ? childPartsV1.length
                : 0
              : childPartsV2
                ? childPartsV2.length
                : 0
          }
          isFlagPart={partInfo?.is_flagged || false}
          isAdminFlagged={partInfo?.is_admin_flagged || false}
          onFlaggedChange={handleFlagPart}
          activeMenu={menu}
          onMenuChange={setMenu}
          version={version || "v2"}
          isEditing={isEditing}
          isAdding={isAdding}
          isEditingPartProcess={isEditingPartProcess}
          canEdit={canEdit}
        />
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            width: 2,
            borderRadius: 10,
            display: "flex",
          }}
        />
        <Box width="80%">
          <Box display="flex" justifyContent="space-between">
            <BreakdownInfo
              partInfo={partInfo ?? undefined}
              version={version || "v2"}
            />
            {!isEditingPartProcess &&
            (menu === "child-part" ||
              menu === "material-cost" ||
              menu === "process" ||
              menu === "child-part-process") ? (
              !isAdding && !isEditing ? (
                <Box
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  gap={1}
                >
                  {version === "v2" && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      fontSize="0.9rem"
                      width="100px"
                      mt={2}
                      mb={0.5}
                      mr={1}
                    >
                      Selected {selectedChildPartId?.length || 0} items
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    sx={{
                      ...DefaultSecondaryButton,
                      fontSize: "0.75rem",
                    }}
                    size="small"
                    onClick={handleDownloadReport}
                  >
                    <InsertDriveFile /> Download
                  </Button>
                  {version === "v2" && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          ...DefaultSecondaryButton,
                          fontSize: "0.75rem",
                          color: "#d32f2f",
                          borderColor: "#d32f2f",
                        }}
                        onClick={handleDeactivateChildPart}
                        disabled={selectedChildPartId?.length === 0 || !canEdit}
                      >
                        <Delete /> ลบ Child Part
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          ...DefaultSecondaryButton,
                          fontSize: "0.75rem",
                        }}
                        onClick={() => {
                          handleEditChildPart();
                        }}
                        disabled={selectedChildPartId?.length === 0 || !canEdit}
                      >
                        <EditIcon />
                        แก้ไข Child Part
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          ...DefaultSecondaryButton,
                          fontSize: "0.75rem",
                        }}
                        onClick={() => handleAddChildPart()}
                        disabled={!canEdit}
                      >
                        <Add />
                        เพิ่ม Child Part
                      </Button>
                    </>
                  )}
                </Box>
              ) : version === "v2" ? (
                <Box
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  gap={1}
                >
                  <Button
                    id="cancel-btn"
                    variant="outlined"
                    sx={{ ...NormalSecondaryButton, fontSize: "0.75rem" }}
                    onClick={() => {
                      setIsAdding(false);
                      setIsEditing(false);
                    }}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    id="save-btn"
                    variant="outlined"
                    sx={{ ...DefaultPrimaryButton, fontSize: "0.75rem" }}
                    onClick={() => handleSaveChildPart()}
                  >
                    บันทึก
                  </Button>{" "}
                </Box>
              ) : null
            ) : null}
            {!isAdding &&
            !isEditing &&
            (menu === "part-process" || menu === "part-other-process") ? (
              !isEditingPartProcess ? (
                <Box
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  gap={1}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      ...DefaultSecondaryButton,
                      fontSize: "0.75rem",
                    }}
                    size="small"
                    onClick={handleDownloadReport}
                  >
                    <InsertDriveFile /> Download
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      ...DefaultSecondaryButton,
                      fontSize: "0.75rem",
                    }}
                    onClick={() => {
                      handleEditPartProcess();
                    }}
                    disabled={!canEdit}
                  >
                    <EditIcon />
                    แก้ไข{" "}
                    {menu === "part-process"
                      ? "Part Process"
                      : "Part Other Process"}
                  </Button>
                </Box>
              ) : (
                <Box
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  gap={1}
                >
                  <Button
                    id="cancel-btn"
                    variant="outlined"
                    sx={{ ...NormalSecondaryButton, fontSize: "0.75rem" }}
                    onClick={() => {
                      setIsEditingPartProcess(false);
                    }}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    id="save-btn"
                    variant="outlined"
                    sx={{ ...DefaultPrimaryButton, fontSize: "0.75rem" }}
                    onClick={() => handleSavePartProcess()}
                  >
                    บันทึก
                  </Button>{" "}
                </Box>
              )
            ) : null}
          </Box>
          <Box mt={2}>
            {menu !== "history" && (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {menu !== "part-process" && menu !== "part-other-process" ? (
                    <TextField
                      label="ค้นหา Child Part No. / Child Part Name"
                      variant="outlined"
                      size="small"
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      sx={{
                        width: "30%",
                        "& .MuiInputLabel-root": {
                          fontSize: "0.75rem",
                          color: "#666666",
                          "&.Mui-focused": {
                            color: "primary.main",
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#cccccc",
                          },
                          "&:hover fieldset": {
                            borderColor: "#999999",
                          },
                        },
                      }}
                    />
                  ) : (
                    <Box></Box>
                  )}
                  <Box display="flex" gap={1} alignItems="center">
                    {version === "v1" ? (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            ...DefaultSecondaryButton,
                            height: "32px",
                            fontSize: "0.75rem",
                            color: "secondary.main",
                            borderColor: "secondary.main",
                          }}
                          onClick={() =>
                            setManageMaterialProcessModalOpen(true)
                          }
                        >
                          จัดการ Material Process
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            ...DefaultSecondaryButton,
                            height: "32px",
                            fontSize: "0.75rem",
                            color: "secondary.main",
                            borderColor: "secondary.main",
                          }}
                          onClick={() => setManagePartProcessModalOpen(true)}
                        >
                          จัดการ Part Process
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            ...DefaultSecondaryButton,
                            height: "32px",
                            fontSize: "0.75rem",
                            color: "secondary.main",
                            borderColor: "secondary.main",
                          }}
                          onClick={() =>
                            setManageMaterialProcessModalOpen(true)
                          }
                        >
                          จัดการ Material Process
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            ...DefaultSecondaryButton,
                            height: "32px",
                            fontSize: "0.75rem",
                            color: "secondary.main",
                            borderColor: "secondary.main",
                          }}
                          onClick={() =>
                            setManageChildPartProcessModalOpen(true)
                          }
                        >
                          จัดการ Child Part Process
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            ...DefaultSecondaryButton,
                            height: "32px",
                            fontSize: "0.75rem",
                            color: "secondary.main",
                            borderColor: "secondary.main",
                          }}
                          onClick={() => setManagePartProcessModalOpen(true)}
                        >
                          จัดการ Part Process
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            ...DefaultSecondaryButton,
                            height: "32px",
                            fontSize: "0.75rem",
                            color: "secondary.main",
                            borderColor: "secondary.main",
                          }}
                          onClick={() =>
                            setManagePartOtherProcessModalOpen(true)
                          }
                        >
                          จัดการ Part Other Process
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
                {(menu === "child-part" ||
                  menu === "material-cost" ||
                  menu === "process" ||
                  menu === "child-part-process") && (
                  <BreakdownTable
                    rows={filteredChildParts || []}
                    isLoading={
                      version === "v1"
                        ? isChildPartsV1Loading
                        : isChildPartsV2Loading
                    }
                    supplierCode={supplierCode}
                    isAdminPct={supplier?.is_admin_pct || false}
                    isProfitPct={supplier?.is_profit_pct || false}
                    partId={partId ? Number(partId) : 0}
                    partNo={partInfo?.part_number || ""}
                    rowCount={totalChildPartsCount}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={(_event, newPage) => setPage(newPage)}
                    handleChangeRowsPerPage={(event) =>
                      setRowsPerPage(parseInt(event.target.value, 10))
                    }
                    menu={menu}
                    version={version || "v2"}
                    isEditing={isEditing}
                    isAdding={isAdding}
                    newChildPart={newChildPart}
                    setNewChildPart={setNewChildPart}
                    selectedChildPartId={selectedChildPartId}
                    setSelectedChildPartId={setSelectedChildPartId}
                    editedChildParts={editedChildParts}
                    setEditedChildParts={setEditedChildParts}
                    validationErrors={validationErrors}
                    setFlaggedReason={setFlaggedReason}
                  />
                )}
                {(menu === "part-process" || menu === "part-other-process") && (
                  <PartProcess
                    isOtherProcess={menu === "part-other-process"}
                    data={
                      menu === "part-process"
                        ? partProcessV2
                        : partOtherProcessV2
                    }
                    isLoading={
                      menu === "part-process"
                        ? isPartProcessV2Loading
                        : isPartOtherProcessV2Loading
                    }
                    isEditingPartProcess={isEditingPartProcess}
                    isAdminPct={supplier?.is_admin_pct || false}
                    isProfitPct={supplier?.is_profit_pct || false}
                    formData={formDataPartProcess}
                    setFormData={setFormDataPartProcess}
                    setFlaggedReason={setFlaggedReason}
                  />
                )}
              </>
            )}

            {menu === "history" && (
              <PartPriceHistoryTable rows={partPriceHistory || []} />
            )}
          </Box>
        </Box>
      </Box>

      <ManageMaterialProcessModal
        open={manageMaterialProcessModalOpen}
        handleClose={() => setManageMaterialProcessModalOpen(false)}
        supplierCode={supplierCode || ""}
        partId={partId ? Number(partId) : 0}
        version={version === "v1" ? "v1" : "v2"}
        canEdit={canEdit}
      />

      <ManagePartProcessModal
        open={managePartProcessModalOpen}
        handleClose={() => setManagePartProcessModalOpen(false)}
        supplierCode={supplierCode || ""}
        partId={partId ? Number(partId) : 0}
        version={version === "v1" ? "v1" : "v2"}
        canEdit={canEdit}
      />

      <ManageChildPartProcessModal
        open={manageChildPartProcessModalOpen}
        handleClose={() => setManageChildPartProcessModalOpen(false)}
        partId={partId ? Number(partId) : 0}
        version={version === "v1" ? "v1" : "v2"}
        canEdit={canEdit}
      />

      <ManagePartOtherProcessModal
        open={managePartOtherProcessModalOpen}
        handleClose={() => setManagePartOtherProcessModalOpen(false)}
        partId={partId ? Number(partId) : 0}
        canEdit={canEdit}
      />

      <UnFlaggedModal
        open={isOpenUnFlagModal}
        selectedParts={[
          {
            partId: partId ? Number(partId) : 0,
          },
        ]}
        handleClose={() => setIsOpenUnFlagModal(false)}
        requiredApprovalId={true}
      />
    </Box>
  );
};

export default BreakdownManagement;
